import type { NativeEmbedParameterValues } from '@atlaskit/native-embeds-common/types';
import type { ClientId } from '@atlassian/rovo-content-bridge-api/client-types';

import type { NativeEmbedChildCommand } from '../commands';
import type { NativeEmbedFeature } from '../features/main';
import type { ResolvedExperienceMeta, UiConfig } from '../ui-config';
import type { URLMetaData } from '../url/types';
import type {
	EventCallback,
	ExperienceEventMap,
	ExperienceEventSubscriptionOptions,
} from './experience-event-emitter';
import type { ExperienceEventEmitter } from './experience-event-emitter';

export type NativeEmbedClientId = ClientId;

/**
 * View mode for the embed container. Aligned with parent→child view-mode-change events.
 */
export type NativeEmbedViewMode = 'regular' | 'maximize';

/**
 * Branded type for experience manifest IDs. Use {@link experienceManifestId} to create.
 */
export type ExperienceManifestId = string & { __experienceManifestId: symbol };

/**
 * Converts a string to an {@link ExperienceManifestId}.
 */
export const experienceManifestId = (s: string): ExperienceManifestId => s as ExperienceManifestId;

/**
 * DTO for creating an experience: manifest plus resolved URL metadata.
 * Used by the facade (which resolves the URL once) and passed to the experience service.
 */
export type ExperienceCreateDto = {
	manifest: ExperienceManifest;
	urlMeta: URLMetaData;
};

/**
 * Optional, forward-compatible context passed to {@link ExperienceManifest.transformUrl}
 * (and {@link Experience.transformUrl}) as the third argument.
 *
 * Experiences should treat every field as optional and ignore values they do
 * not care about. New fields may be added over time without breaking existing
 * experiences.
 */
export type TransformUrlOptions = {
	/**
	 * When true, the host page that contains the embed is currently in
	 * edit mode (e.g. a Confluence page editor with `editorViewMode` ===
	 * `'edit'`). When false or omitted, the host is in view mode or the
	 * edit state is unknown.
	 *
	 * Experiences can use this to forward edit-mode hints to the embedded
	 * iframe (for example, by appending an `enable-edits` query param).
	 */
	isEditMode?: boolean;
};

/**
 * Context supplied when an experience decides whether a click inside its iframe
 * should restore focus to the host editor.
 */
export type ShouldFocusOnClickOptions = {
	/** Whether the native embed already has a ProseMirror node selection. */
	isSelected?: boolean;
};

/**
 * Declares which URL-build site(s) a query param is included in.
 *
 * - `'always'` (default): included at every build site (inline, shared-iframe
 *   full screen, and legacy full screen).
 * - `'exceptLegacyFullscreen'`: included inline and in shared-iframe full
 *   screen, but omitted from the legacy full-screen path's own URL build.
 *
 * Named for the URL-build site rather than the `isFullScreen` boolean: the
 * shared-iframe full-screen path reuses the inline URL and never re-builds, so
 * a param that is "absent in full screen" must only be dropped where a separate
 * legacy URL is actually built. There is intentionally no `onlyLegacyFullscreen`
 * state.
 */
export type QueryParamAppliesTo = 'always' | 'exceptLegacyFullscreen';

/**
 * A manifest query-param value. A bare `string` is sugar for
 * `{ value, appliesTo: 'always' }`; the object form carries an explicit
 * {@link QueryParamAppliesTo}.
 */
export type QueryParamValue = string | { appliesTo?: QueryParamAppliesTo; value: string };

/**
 * Map of query-param name → value config. See {@link QueryParamValue}.
 *
 * Resolved per URL-build site via `resolveQueryParams` before the flat result
 * is appended to the iframe `src`.
 */
export type QueryParams = Record<string, QueryParamValue>;

/**
 * Manifest `queryParams` value: either a static {@link QueryParams} map or a
 * **thunk** that returns one.
 *
 * The thunk form is evaluated at URL-build time (not module load), so it can
 * read a feature gate / dynamic config after Statsig has initialized — the same
 * reason `isActive` and `lockResizeAspectRatio` are functions. Use it to gate
 * which params (or which {@link QueryParamValue} form) an experience emits, e.g.
 * behind a killswitch, while keeping `resolveQueryParams` pure.
 */
export type QueryParamsInput = QueryParams | (() => QueryParams);

/**
 * Host capabilities that an experience opts into for comments.
 */
export type ExperienceCommentsConfig = {
	/**
	 * Whether host comment surfaces may be offered for this experience.
	 * Hosts still apply their own rollout, permission, and interaction-state checks.
	 *
	 * @default false
	 */
	enabled: boolean;
};

/**
 * Manifest for registering an experience.
 */
export type ExperienceManifest = {
	/**
	 * This editor-specific option opts the experience into the host's maximum-width
	 * capability. When true, the editor may allow the embed to grow to the surrounding
	 * text layout width when its host-controlled rollout configuration is also enabled.
	 *
	 * @default false
	 */
	allowMaxWidth?: boolean;
	/**
	 * When true, the embed can be resized vertically via a bottom drag handle
	 * in the editor. When false (default), only horizontal resize is available.
	 *
	 * @default false
	 */
	allowVerticalResize?: boolean;
	/**
	 * Optional host-comment capability. Experiences are not commentable when this
	 * is omitted or when `enabled` is false.
	 */
	comments?: ExperienceCommentsConfig;
	/**
	 * When true, ensures that the manifest's `queryParams` are present in the
	 * final iframe `src` after the Smart Card / Object Resolver Service resolves
	 * the URL. If any specified query parameters are missing from the resolved
	 * URL, they are appended directly to the iframe's `src` attribute.
	 *
	 * This is useful when the ORS returns a preview URL that drops query
	 * parameters that were originally provided.
	 *
	 * Only effective when `queryParams` is also specified.
	 *
	 * @default false
	 */
	enforceQueryParams?: boolean;
	/**
	 * Allows for handling child commands for the embed has sent back.
	 * This is used to extend the experience with additional functionality.
	 */
	/**
	 * Returns a stable identifier for the *content* a URL points at, independent of
	 * any part of the URL that can change without changing the content — vanity
	 * path segments, transient query params, and so on.
	 *
	 * Hosts use this to decide whether a URL change is a genuine navigation or
	 * cosmetic churn. Without it, a host that keys its lifecycle on the raw URL
	 * string must assume every change is a navigation: destroy the experience,
	 * drop the iframe from the DOM and cold-boot the child.
	 *
	 * The concrete case this exists for: a Confluence whiteboard created by a Rovo
	 * Remix is reparented into the user's space after the diagram has been drawn,
	 * which rewrites the embed URL's space segment and strips its streaming params.
	 * The board itself is untouched, so reloading it is pure waste.
	 *
	 * Return `undefined` when identity cannot be determined; callers must then fall
	 * back to treating the URL itself as the identity.
	 */
	getContentIdentity?: (url: string) => string | undefined;
	handleChildCommands: (experience: Experience, commands: NativeEmbedChildCommand) => void;
	id: ExperienceManifestId;
	isActive: (urlMeta: URLMetaData) => boolean;
	isUrlMatch: (url: string) => boolean;
	/**
	 * When false, horizontal resize will not lock the vertical height to the
	 * original aspect ratio. The embed resizes only on the horizontal axis
	 * and the height is left unchanged after a drag.
	 *
	 * @default true
	 */
	/**
	 * Evaluated at runtime (e.g. to read a feature gate after Statsig initializes).
	 * When omitted, behaves as `() => true`.
	 */
	lockResizeAspectRatio?: () => boolean;
	/**
	 * Container width (in pixels) below which the embed iframe is replaced with a
	 * SmartCard block card in view mode. Lets an experience opt into a smaller
	 * (or larger) narrow-fallback threshold than the platform default.
	 *
	 * For example, MAUI apps that remain usable at very small widths can set this
	 * to `50` so they keep rendering the native embed instead of collapsing to a
	 * card. When omitted, the platform default (`480`) is used.
	 *
	 * @default 480
	 */
	narrowEmbedThreshold?: number;
	/**
	 * Optional per-experience overrides for native embed parameter defaults.
	 */
	parameterDefaults?: Partial<NativeEmbedParameterValues>;
	/**
	 * Optional query parameters to append to the iframe URL.
	 * Applied after URL resolution and any `transformUrl` call.
	 *
	 * Each value is a {@link QueryParamValue}: a bare string (applies at every
	 * URL-build site) or an object with an explicit {@link QueryParamAppliesTo}.
	 * Resolved per build site via `resolveQueryParams`.
	 *
	 * May be a thunk ({@link QueryParamsInput}) evaluated at URL-build time, so
	 * an experience can gate which params it emits (e.g. behind a killswitch).
	 */
	queryParams?: QueryParamsInput;
	/**
	 * When true (default), the embed URL is resolved via the Linking Platform
	 * using the Smart Card component. When false, the URL is rendered directly
	 * in a basic iframe without link resolution.
	 *
	 * @default true
	 */
	resolveViaLinkingPlatform?: boolean;
	/**
	 * Controls whether the editor should call `view.focus()` after the user clicks inside
	 * the native embed iframe (restoring keyboard focus to the editor). Return `false` to
	 * keep focus inside the iframe — e.g. for live docs or pages in edit mode where the
	 * iframe handles focus internally.
	 *
	 * When omitted, defaults to `true` (focus is always restored to the editor).
	 *
	 * @param url - The URL of the embed being clicked.
	 * @param meta - Resolved experience metadata (e.g. documentType from JSON-LD).
	 * @param options - Selection context for the clicked native embed.
	 * @default true
	 */
	shouldFocusOnClick?: (
		url: string,
		meta: ResolvedExperienceMeta,
		options?: ShouldFocusOnClickOptions,
	) => boolean;
	/**
	 * Features this experience requests during the host↔child handshake.
	 */
	supportedFeatures: Set<NativeEmbedFeature>;
	/**
	 * Optional URL transformer that allows the experience to modify the URL
	 * before it is loaded in the iframe. Can be used to add query parameters.
	 * Called after URL resolution but before the URL is passed to the iframe.
	 *
	 * @param url - The URL to transform.
	 * @param isFullScreen - Whether the URL is being transformed for the fullscreen dialog.
	 * @param options - Optional, forward-compatible context (e.g. `isEditMode`).
	 *   New fields may be added over time; experiences should treat each field as
	 *   optional and ignore values they do not care about.
	 */
	transformUrl?: (url: string, isFullScreen?: boolean, options?: TransformUrlOptions) => string;
	uiConfig: UiConfig;
};

/**
 * Host-side instance for a single native embed. One instance per embed; `id` uniquely
 * identifies it.
 *
 * Used to be passed down through the React components to specify the experience.
 *
 * Obtained by calling `getExperienceService().registerExperience(manifest)` with an
 * {@link ExperienceManifest}. Each registered embed gets its own Experience instance.
 */
export interface Experience {
	/**
	 * When true, ensures that `queryParams` are present in the final iframe
	 * `src` after Smart Card / ORS URL resolution. Missing params are appended
	 * directly to the iframe element's `src` attribute.
	 *
	 * @default false
	 */
	enforceQueryParams: boolean;
	id: NativeEmbedClientId;
	/**The experience decides if it wants to handle this native embed URL based on the URL metadata */
	isActive: (urlMeta: URLMetaData) => boolean;
	/** The experience decides if it wants to handle this native embed URL based on the URL metadata */
	isUrlMatch: (urlMeta: string) => boolean;
	/**
	 * When false, horizontal resize will not lock the vertical height to the
	 * original aspect ratio. The embed resizes only on the horizontal axis
	 * and the height is left unchanged after a drag.
	 *
	 * @default true
	 */
	lockResizeAspectRatio: () => boolean;
	/**
	 * The manifest ID for this experience — identifies the embed type
	 * (e.g. `'maui'`, `'maui-whiteboard'`).
	 */
	manifestId?: ExperienceManifestId;
	metaData?: URLMetaData;
	/**
	 * Container width (in pixels) below which the embed iframe is replaced with a
	 * SmartCard block card in view mode. Forwarded unchanged from the manifest.
	 * When omitted, the platform default (`480`) is used.
	 *
	 * @default 480
	 */
	narrowEmbedThreshold?: number;
	/**
	 * Subscribe to an event from this experience's child embed.
	 *
	 * Returns a synchronous unsubscribe function. Call it in your cleanup
	 * (e.g. the return value of a `useEffect`) to avoid stale listeners.
	 *
	 * Events are only emitted when the corresponding feature is agreed during
	 * the host↔child handshake. If the feature is not agreed, subscribing is
	 * harmless — the callback simply never fires.
	 *
	 * @example
	 * ```tsx
	 * useEffect(() => {
	 *     return experience.on('click-inside', () => {
	 *         // handle the click — e.g. select the editor node
	 *     });
	 * }, [experience]);
	 * ```
	 */
	on<K extends keyof ExperienceEventMap>(
		event: K,
		callback: EventCallback<ExperienceEventMap[K]>,
		options?: ExperienceEventSubscriptionOptions,
	): () => void;
	/**
	 * Optional query parameters to append to the iframe URL.
	 *
	 * Forwarded unchanged from the manifest (including the thunk form). Each
	 * value is a {@link QueryParamValue}; resolved per URL-build site via
	 * `resolveQueryParams`.
	 */
	queryParams?: QueryParamsInput;
	/**
	 * When true (default), the embed URL is resolved via the Linking Platform
	 * in a basic iframe without link resolution.
	 *
	 * @default true
	 */
	resolveViaLinkingPlatform: boolean;
	supportedFeatures: Promise<Set<NativeEmbedFeature>>;
	/**
	 * Optional URL transformer that allows the experience to modify the URL
	 * before it is loaded in the iframe. Can be used to add query parameters.
	 *
	 * @param url - The URL to transform.
	 * @param isFullScreen - Whether the URL is being transformed for the fullscreen dialog.
	 * @param options - Optional, forward-compatible context (e.g. `isEditMode`).
	 *   New fields may be added over time; experiences should treat each field as
	 *   optional and ignore values they do not care about.
	 */
	transformUrl?: (url: string, isFullScreen?: boolean, options?: TransformUrlOptions) => string;

	uiConfig: UiConfig;
}

/**
 * Internal extension of Experience with write access to the event emitter.
 *
 * Used exclusively inside the service layer (ExperienceService). Never
 * exported from the package — consumers only see `Experience`.
 *
 * DO NOT export this type from any barrel file (index.ts).
 */
export interface InternalExperience extends Experience {
	/** @internal */
	readonly events: ExperienceEventEmitter;
}
